import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Search, ClipboardList, BarChart3, Pencil, Trash2,
  Star, Users, Clock, Eye, X, Send, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import NPSSurveyPage from './NPSSurveyPage';

const API = process.env.REACT_APP_BACKEND_URL;

const SURVEY_TYPES = [
  { value: 'csat', label: 'CSAT', desc: 'Customer Satisfaction' },
  { value: 'nps', label: 'NPS', desc: 'Net Promoter Score' },
  { value: 'custom', label: 'Custom', desc: 'Custom Questions' },
];
const SURVEY_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-600' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'closed', label: 'Closed', color: 'bg-red-100 text-red-600' },
];

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

function SurveyModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ title: '', description: '', survey_type: 'csat', status: 'draft', questions: [] });
  const [saving, setSaving] = useState(false);
  const [newQ, setNewQ] = useState('');

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || '',
        description: editing.description || '',
        survey_type: editing.survey_type || 'csat',
        status: editing.status || 'draft',
        questions: editing.questions || [],
      });
    } else {
      setForm({ title: '', description: '', survey_type: 'csat', status: 'draft', questions: [] });
    }
    setNewQ('');
  }, [editing, open]);

  const addQuestion = () => {
    if (!newQ.trim()) return;
    setForm(p => ({ ...p, questions: [...p.questions, { text: newQ.trim(), type: 'rating' }] }));
    setNewQ('');
  };

  const removeQuestion = (idx) => {
    setForm(p => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/service/surveys/${editing.id}`, form)
        : await axios.post(`${API}/api/service/surveys`, form);
      toast.success(editing ? 'Survey updated' : 'Survey created');
      onSaved(data, !!editing);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-teal-500" />
            {editing ? 'Edit' : 'New'} Survey
          </DialogTitle>
          <DialogDescription>Create a customer feedback survey</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Customer Satisfaction Q1" data-testid="survey-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Brief survey description..." data-testid="survey-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.survey_type} onValueChange={v => setForm(p => ({ ...p, survey_type: v }))}>
                <SelectTrigger data-testid="survey-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SURVEY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label} - {t.desc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="survey-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SURVEY_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Questions Builder */}
          <div>
            <Label>Questions</Label>
            <div className="space-y-1.5 mt-1">
              {form.questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2" data-testid={`question-${i}`}>
                  <span className="text-xs text-gray-400 font-mono w-5">{i + 1}.</span>
                  <span className="text-sm text-gray-700 flex-1">{q.text}</span>
                  <Badge variant="outline" className="text-[9px]">{q.type}</Badge>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeQuestion(i)}>
                    <X className="h-3 w-3 text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={newQ} onChange={e => setNewQ(e.target.value)} placeholder="Add a question..." className="flex-1 text-sm" data-testid="new-question-input"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addQuestion(); } }} />
              <Button type="button" variant="outline" size="sm" onClick={addQuestion} data-testid="add-question-btn">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-survey-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create Survey'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SurveyDetail({ survey, onClose }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!survey) return;
    axios.get(`${API}/api/service/surveys/${survey.id}/responses`)
      .then(r => setResponses(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [survey]);

  if (!survey) return null;
  const statusInfo = SURVEY_STATUSES.find(s => s.value === survey.status) || SURVEY_STATUSES[0];
  const typeInfo = SURVEY_TYPES.find(t => t.value === survey.survey_type) || SURVEY_TYPES[0];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="survey-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-gray-900 line-clamp-1">{survey.title}</h2>
            <p className="text-xs text-gray-400">{typeInfo.desc}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X className="h-4 w-4" /></Button>
        </div>
        <div className="px-5 py-3 flex items-center gap-2 border-b">
          <Badge className={`${statusInfo.color} text-xs`}>{statusInfo.label}</Badge>
          <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
          <span className="text-xs text-gray-400 ml-auto">{survey.response_count} responses</span>
        </div>
        {survey.description && (
          <div className="px-5 py-3 border-b">
            <p className="text-sm text-gray-600">{survey.description}</p>
          </div>
        )}
        {/* Questions */}
        <div className="px-5 py-3 border-b">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Questions ({(survey.questions || []).length})</p>
          <div className="space-y-2">
            {(survey.questions || []).map((q, i) => (
              <div key={i} className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-xs text-gray-400 font-mono mt-0.5">{i + 1}.</span>
                <span className="text-sm text-gray-700">{q.text}</span>
              </div>
            ))}
            {(!survey.questions || survey.questions.length === 0) && <p className="text-xs text-gray-400 text-center py-2">No questions added</p>}
          </div>
        </div>
        {/* Stats */}
        <div className="px-5 py-3 border-b">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-teal-50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-teal-700">{survey.response_count || 0}</p>
              <p className="text-[10px] text-teal-600">Responses</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-amber-700">{survey.avg_score || '—'}</p>
              <p className="text-[10px] text-amber-600">Avg Score</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-blue-700">{(survey.questions || []).length}</p>
              <p className="text-[10px] text-blue-600">Questions</p>
            </div>
          </div>
        </div>
        {/* Responses */}
        <div className="px-5 py-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Recent Responses</p>
          {loading ? (
            <div className="flex justify-center py-4"><div className="w-6 h-6 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" /></div>
          ) : responses.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No responses yet</p>
          ) : (
            <div className="space-y-2">
              {responses.slice(0, 10).map(r => (
                <div key={r.id} className="bg-gray-50 rounded-lg p-3" data-testid={`response-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-700">{r.respondent_name || 'Anonymous'}</span>
                    <span className="text-[10px] text-gray-400">{timeAgo(r.created_at)}</span>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {(r.answers || []).map((a, i) => (
                      <Badge key={i} variant="outline" className="text-[9px]">
                        Q{i + 1}: {a.score !== undefined ? `${a.score}/5` : a.text || '—'}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SurveysPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'surveys';

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="surveys-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
          <ClipboardList className="h-5 w-5 text-teal-500" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Surveys & Feedback<PageInfoTooltip pageKey="surveys" /></h1>
          <p className="text-xs text-gray-400">Collect feedback and measure customer satisfaction</p>
        </div>
      </div>

      <Tabs defaultValue={initialTab} onValueChange={v => setSearchParams(v === 'surveys' ? {} : { tab: v }, { replace: true })}>
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="surveys" className="gap-1.5 text-xs" data-testid="surveys-tab-surveys"><ClipboardList className="h-3.5 w-3.5" />Surveys</TabsTrigger>
          <TabsTrigger value="nps" className="gap-1.5 text-xs" data-testid="surveys-tab-nps"><BarChart3 className="h-3.5 w-3.5" />NPS</TabsTrigger>
        </TabsList>
        <TabsContent value="surveys"><SurveyListView /></TabsContent>
        <TabsContent value="nps"><NPSSurveyPage /></TabsContent>
      </Tabs>
    </div>
  );
}

function SurveyListView() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const fetchSurveys = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const { data } = await axios.get(`${API}/api/service/surveys`, { params,  });
      const filtered = search
        ? data.filter(s => s.title.toLowerCase().includes(search.toLowerCase()))
        : data;
      setSurveys(filtered);
    } catch { toast.error('Failed to load surveys'); }
    finally { setLoading(false); }
  }, [search, filterStatus]);

  useEffect(() => { fetchSurveys(); }, [fetchSurveys]);

  const handleSaved = (s, isUpdate) => {
    if (isUpdate) setSurveys(p => p.map(x => x.id === s.id ? s : x));
    else setSurveys(p => [s, ...p]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this survey and all responses?')) return;
    try {
      await axios.delete(`${API}/api/service/surveys/${id}`);
      setSurveys(p => p.filter(s => s.id !== id));
      toast.success('Survey deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const activeCount = surveys.filter(s => s.status === 'active').length;
  const totalResponses = surveys.reduce((sum, s) => sum + (s.response_count || 0), 0);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs text-gray-400 flex-1">{surveys.length} surveys · {activeCount} active · {totalResponses} responses</p>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-36 h-9 text-sm" data-testid="survey-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-28 h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {SURVEY_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-teal-600 hover:bg-teal-700 h-9 gap-1.5" data-testid="new-survey-btn">
          <Plus className="h-4 w-4" /> New Survey
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
        </div>
      ) : surveys.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No surveys yet</h3>
            <p className="text-sm text-gray-400 mb-4">Create your first survey to gather customer feedback</p>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-teal-600 hover:bg-teal-700 gap-1.5">
              <Plus className="h-4 w-4" /> Create Survey
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {surveys.map(s => {
            const statusInfo = SURVEY_STATUSES.find(st => st.value === s.status) || SURVEY_STATUSES[0];
            const typeInfo = SURVEY_TYPES.find(t => t.value === s.survey_type) || SURVEY_TYPES[0];
            return (
              <Card key={s.id} className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedSurvey(s)} data-testid={`survey-card-${s.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 line-clamp-1">{s.title}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge className={`${statusInfo.color} text-[9px]`}>{statusInfo.label}</Badge>
                        <Badge variant="outline" className="text-[9px]">{typeInfo.label}</Badge>
                      </div>
                    </div>
                  </div>
                  {s.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{s.description}</p>}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-gray-700">{(s.questions || []).length}</p>
                      <p className="text-[9px] text-gray-400">Questions</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-gray-700">{s.response_count || 0}</p>
                      <p className="text-[9px] text-gray-400">Responses</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-gray-700">{s.avg_score || '—'}</p>
                      <p className="text-[9px] text-gray-400">Avg Score</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                      <Clock className="h-2.5 w-2.5" />{timeAgo(s.created_at)}
                    </span>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditing(s); setModalOpen(true); }}>
                        <Pencil className="h-3 w-3 text-gray-400" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(s.id)}>
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SurveyModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
      {selectedSurvey && <SurveyDetail survey={selectedSurvey} onClose={() => setSelectedSurvey(null)} />}
    </div>
  );
}
